/**
 * Cashflow & Age Pension — the module for a client with no care event, on the shared spine (cashflow
 * rebuild, 21 Oct 2026; the agreed mock-up docs/ux-review/modules/cp/mockup, the village as built).
 *
 * See cashflowPensionScenario.ts for why this exists and why it is deliberately general. In short: CHSP has no
 * means test, so the care cost is an INPUT the client gives us rather than an OUTPUT we calculate, and the
 * question worth answering becomes "can we afford this, and can we do better with what we have?"
 *
 * THE SPINE, as aged care, Support at home and the village:
 *   1. Your money today — the support cost first (it changes the numbers), then the four numbers.
 *   2. What it means — why the Age Pension is what it is, the money each year, what is left to the family.
 *   3. Ways to improve it — three plain questions, then the plans that lead each goal against changing nothing,
 *      with Explain this, What could go wrong and Choose; every way side by side; "Build a plan yourself" as a
 *      link to the ledger (no mode chooser: owner, 20 Oct 2026).
 *   Then the plan page, where the module opens once a plan is chosen, and the advice document.
 *
 * THE DOOR (owner, 19 Oct 2026): Support at home's step 1 while nobody being priced is assessed; Home and the
 * menu list it only once a plan is saved, with "Start a new model" on its Home tile. `cameFrom` says which
 * door was used, so Back returns through it.
 *
 * THE PAGE NEVER HOLDS THE FILE'S OWN WRITER (a test enforces it): the support cost is the client's own figure and is
 * never written to the file. It writes through three narrow writers App gives it — the income-only tick, the
 * guaranteed-income answers, and step 3's own answers (cashflowGoalsSetter) — each for its keys alone.
 */
import { chosenPlanOutOfDate, markRecordOutOfDate, NOT_FOUND_ON_RETEST, profileFingerprint, retestChosenRecord } from '../../utils/chosenPlanRefresh.ts';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { withChosenPlan } from '../../strategy/chosenPlan.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { IncomeOnlyBeforeChoose, incomeOnlyHeld, isIncomeOnlyAnnuity } from '../../components/strategy/IncomeOnlyTick.tsx';
import type { GuaranteedIncomeGoals } from '../../components/strategy/GuaranteedIncomeQuestions.tsx';
import AnnuityFamilyPanel, { familyPanelKindOf } from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { annuityQuoteShapeOf, annuityQuoteNote, type AnnuityQuote } from '../../strategy/annuityQuote.ts';
import StrategyRisksPanel from '../../components/strategy/StrategyRisksPanel.tsx';
import AnalyserSteps from '../../components/strategy/AnalyserSteps.tsx';
import { PlanPiecesCard } from '../../components/narrative/PlanPieces.tsx';
import { buildStrategyRisks, type RiskKind } from '../../strategy/strategyRisks.ts';
import { planRunwayCopy, summariseFunding } from '../../components/insights/fundingSummary.tsx';
import { buildMoneyStory, MoneyStoryBlock } from '../agedCare/moneyStory.tsx';
import PlanExplanationModal from '../agedCare/PlanExplanationModal.tsx';
import type { GoalFocusId } from '../agedCare/EngineGoalControl.tsx';
import { buildSoaGuaranteedIncome } from '../advice/soaGuaranteedIncome.ts';
import { buildCashflowPensionSoaSnapshot } from './cpSoa.ts';
import { openModuleSoa } from '../advice/moduleSoa.ts';
import { formatCurrency } from '../../utils/index.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import {
    runCashflowProjections,
    summarisePosition,
    type CashflowYear,
    type PositionSummary,
    type SupportCostInput,
    type CashflowPensionResult,
    type ManualAllocation,
    EMPTY_ALLOCATION,
    DEFAULT_ANNUITY_TERMS,
    type ManualAnnuityTerms,
    liquidSourcesOf,
    allocationTotal,
    manualCandidateFrom,
    CASHFLOW_REOPEN_KEY,
    peekReopenedCashflowPlan,
    reopenedPlanFrom,
    payableDebtsForCashflow,
    type CashflowGoals,
} from './cashflowPensionScenario.ts';
import { generateCashflowPensionCandidates, cashflowReserveOf, CASHFLOW_LEVERS, type CashflowPensionCandidate } from './cashflowPensionCandidates.ts';
import { MIN_ANNUITY_PURCHASE } from './CashflowManualControl.tsx';
import { EngineAnnuityTerms, AnnuityQuoteControl } from '../../components/strategy/AnnuityTerms.tsx';
import { evaluateCashflowPensionCandidates, calculateFinancials } from '../../services/calculationApi.ts';
import { cpPlanWords, cpKindsOf, cpPiecesOf, cpWhyLines, cpSpentOnWords, cpAmountsOf, cpLedgerLines, cpFirstYearOf, cpHorizonWarningOf, type CpCandidateLike } from './cpPlanWords.ts';
import { CauseEffectLedger, ChangeOrText, putTogetherLine } from '../../components/strategy/CauseEffectLedger.tsx';
import ProjectionsModal from '../agedCare/ProjectionsModal.tsx';
import { CP_START_FRESH_KEY, chosenCpPlanOf, cpCardOf, takeCpSessionFlag, supportChangedSince, type CpPlanCardRecord } from './cpChosenPlan.ts';
import CpStep1 from './CpStep1.tsx';
import CpStep2 from './CpStep2.tsx';
import CpQuestions, { type CpAnswers } from './CpQuestions.tsx';
import CpPlansView, { cpLeadersOf, CANNOT_CHOOSE_ERRORED } from './CpPlansView.tsx';
import CpHub from './CpHub.tsx';
import CpBuildPlan from './CpBuildPlan.tsx';
import { money } from './cpUi.tsx';
import DebtsInPlanNote from '../../components/strategy/DebtsInPlanNote.tsx';
import { compactStoryRows } from '../agedCare/moneyStory.tsx';

/** What a cashflow plan pays off (its shared `debtPayoff`). */
const cpDebtClearedBy = (c: { debtPayoff?: unknown } | null | undefined): number =>
    Object.values(((c?.debtPayoff as { payments?: Record<string, unknown> } | undefined)?.payments) || {}).reduce<number>((t, n) => t + (Number(n) || 0), 0);

export interface CashflowPensionPageProps {
    setActivePage: (page: string) => void;
    factFind: DraftFactFind;
    /**
     * Records the client's tick that nothing comes back from an income-only annuity (goals.incomeOnlyUnderstood).
     * A writer for that key alone: the support cost stays the client's own figure and is never written.
     */
    onIncomeOnlyUnderstoodChange?: (confirmed: boolean, amount: number) => void;
    /**
     * Writes the client's answers to the guaranteed-income question (GuaranteedIncomeQuestions) — those goals
     * keys alone (cashflowPensionScenario.guaranteedIncomeGoalsSetter). Not given: the question is not asked.
     */
    onGuaranteedIncomeGoals?: (patch: GuaranteedIncomeGoals) => void;
    /** Writes step 3's answers — goals.cpEmergencyReserve and goals.cpLevers alone (cashflowGoalsSetter). */
    onCashflowGoals?: (patch: CashflowGoals) => void;
    setSavedStrategies?: React.Dispatch<React.SetStateAction<Array<Record<string, unknown>>>>;
    /** The file's saved plans, so the chosen cashflow plan opens on its plan page from any door. */
    savedStrategies?: ReadonlyArray<Record<string, unknown>>;
    /**
     * Where the client came from, so the copy can acknowledge it and Back can return there:
     * 'support_at_home' (the Support at Home step 1) or 'home' (Home or the client menu).
     */
    cameFrom?: string;
    /**
     * Called with each generated Statement of Advice (the strategy plus its document snapshot) so it
     * lands on the server's advice register like aged care's (owner, 2 Oct 2026). App.tsx decides where.
     */
    onSoaGenerated?: (document: Record<string, unknown>) => void;
}

const MANUAL_PLAN_ERRORED = 'We couldn’t work your plan out on your figures, so it can’t be saved. Try different amounts.';

/** The chosen plan as the page holds it: its card, and how to rebuild it. */
interface ChosenState {
    card: CpPlanCardRecord;
    createdAt: string | null;
    id: string | null;
    soaPreparedAt: string | null;
    engineId: string | null;
    manual: boolean;
    candidate: CpCandidateLike | null;
    /** The years and support it was priced on. */
    years: number;
    /** A hand-built plan's own amounts and guaranteed-income terms, to rebuild it exactly (23 Oct 2026). */
    allocation?: Record<string, unknown> | null;
    annuityTerms?: Record<string, unknown> | null;
    /** A hand-built plan customised from a tested one ("Customise this plan", 25 Oct 2026): that plan's title. */
    customisedFrom?: string | null;
}

const chosenStateOf = (record: Record<string, unknown> | null | undefined): ChosenState | null => {
    const card = cpCardOf(record);
    if (!record || !card) return null;
    const inputs = (record.inputs || {}) as Record<string, unknown>;
    return {
        card,
        createdAt: record.createdAt ? String(record.createdAt) : null,
        id: record.id != null ? String(record.id) : null,
        soaPreparedAt: record.soaPreparedAt ? String(record.soaPreparedAt) : null,
        engineId: inputs.engineOptionId != null ? String(inputs.engineOptionId) : null,
        manual: (inputs.source || record.source) === 'manual',
        candidate: (inputs.candidate as CpCandidateLike | null) ?? null,
        allocation: (inputs.allocation as Record<string, unknown> | null | undefined) ?? null,
        annuityTerms: (inputs.annuityTerms as Record<string, unknown> | null | undefined) ?? null,
        customisedFrom: typeof record.customisedFrom === 'string' ? record.customisedFrom : null,
        years: Number(inputs.legacyYears) || card.horizonYears,
    };
};

/** A tested plan as the hand-built ledger holds it (Customise this plan). */
function allocationFromCandidate(c: CpCandidateLike | null | undefined): { allocation: ManualAllocation; terms: ManualAnnuityTerms } {
    const m = cpAmountsOf(c);
    const a = (c?.annuity || {}) as { ownership?: unknown; reversionary?: unknown; indexation?: unknown; incomeOverride?: unknown };
    return {
        allocation: { ...EMPTY_ALLOCATION, funeralBond: m.funeralBond, gift: m.gift, annuity: m.annuity, debtRepayment: m.debt, superContribution: m.superTotal, investmentBond: m.investmentBond },
        terms: {
            ...DEFAULT_ANNUITY_TERMS,
            residualCapital: m.annuity > 0 ? m.residual : DEFAULT_ANNUITY_TERMS.residualCapital,
            annuityType: m.annuityType,
            owner: a.ownership === 'partner' ? 'partner' : a.ownership === 'joint' ? 'joint' : 'client',
            reversionary: !!a.reversionary,
            indexation: !!a.indexation,
            incomeOverride: Number(a.incomeOverride) > 0 ? Number(a.incomeOverride) : null,
        },
    };
}

const CashflowPensionPage: React.FC<CashflowPensionPageProps> = ({ setActivePage, factFind, onIncomeOnlyUnderstoodChange, onGuaranteedIncomeGoals, onCashflowGoals, setSavedStrategies, savedStrategies, cameFrom, onSoaGenerated }) => {
    /*
     * "Start a new model" from Home (one-shot): an empty step 1 and the questions asked again; the chosen plan
     * stays chosen until a new one is chosen.
     */
    const [startFresh] = useState(() => takeCpSessionFlag(CP_START_FRESH_KEY));
    /*
     * A saved plan being reopened: Home's hand-off (CASHFLOW_REOPEN_KEY), or the file's chosen plan when the
     * page is opened from another door. Read once, before any other state, so every input starts where the
     * plan was saved; the hand-off is cleared in an effect, so a second render cannot lose it.
     */
    const [reopened] = useState(() => (startFresh ? null : (peekReopenedCashflowPlan() ?? reopenedPlanFrom(chosenCpPlanOf(savedStrategies)))));
    useEffect(() => {
        try { sessionStorage.removeItem(CASHFLOW_REOPEN_KEY); } catch { /* storage unavailable */ }
    }, []);
    const [chosen, setChosen] = useState<ChosenState | null>(() => chosenStateOf(chosenCpPlanOf(savedStrategies)) ?? (reopened?.card && reopened.chosen ? chosenStateOf({ inputs: { card: reopened.card, engineOptionId: reopened.engineOptionId, legacyYears: reopened.legacyYears, source: reopened.mode === 'manual' ? 'manual' : 'suggestion_engine' }, createdAt: reopened.createdAt, id: reopened.id, soaPreparedAt: reopened.soaPreparedAt }) : null));

    const v = householdVoice(factFind);
    const [view, setView] = useState<'steps' | 'hub'>(() => (reopened?.card && chosen ? 'hub' : 'steps'));
    const [step, setStep] = useState<1 | 2 | 3>(() => (reopened && !reopened.card ? 3 : 1));
    const [asking, setAsking] = useState<boolean>(() => startFresh || (!reopened && (factFind?.goals as { cpLevers?: unknown } | undefined)?.cpLevers == null));
    const [focus, setFocus] = useState<GoalFocusId>('legacy');
    const goToStep = (s: 1 | 2 | 3) => { setView('steps'); setStep(s); try { window.scrollTo?.(0, 0); } catch { /* not in a browser */ } };

    const [years, setYears] = useState<CashflowYear[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [legacyYears, setLegacyYears] = useState(() => reopened?.legacyYears || 10);

    /*
     * The support cost is held HERE, not on the fact-find (owner direction). It is the client's figure, taken
     * from their provider's bill; it is only kept if they choose a plan.
     */
    const [supportAnnual, setSupportAnnual] = useState<number | ''>(() => (reopened && reopened.supportCostAnnual > 0 ? reopened.supportCostAnnual : ''));
    const [supportLabel, setSupportLabel] = useState(() => reopened?.supportCostLabel || 'Support at home services');
    const supportCost: SupportCostInput | null = useMemo(
        () => (typeof supportAnnual === 'number' && supportAnnual > 0 ? { annual: supportAnnual, label: supportLabel } : null),
        [supportAnnual, supportLabel],
    );

    /*
     * NET WORTH TODAY CANNOT DEPEND ON THE SUPPORT COST (review of Joe and Kim Smith's file, 18 Oct 2026:
     * entering a support cost moved "Net worth today" from $3,823,397 to $3,815,398). "Today" is read from the
     * run WITHOUT the support cost; the figures about the year ahead come from the run with it.
     */
    const [todayYears, setTodayYears] = useState<CashflowYear[] | null>(null);
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        Promise.all([
            runCashflowProjections(factFind, { legacyYears, supportCost }),
            supportCost ? runCashflowProjections(factFind, { legacyYears, supportCost: null }) : Promise.resolve(null),
        ])
            .then(([result, withoutSupport]) => {
                if (cancelled) return;
                setYears(result);
                setTodayYears(withoutSupport ?? result);
                setLoading(false);
            })
            .catch((e: unknown) => {
                if (cancelled) return;
                setError(e instanceof Error ? e.message : 'Could not work out your position.');
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [factFind, legacyYears, supportCost]);

    /*
     * WHAT THEY OWN TODAY IS TODAY'S POSITION (live review, 22 Oct 2026): the first projection row closes a year
     * on, with a year's growth in it — $3,089,217 where every other module showed $3,020,000 for the same
     * file. It is read from today's financials, as downsizing and granny flat read it, falling back to the run.
     */
    const [todayNetWorth, setTodayNetWorth] = useState<number | null>(null);
    /* Today's financials whole, too: the year-by-year figures open from them, as downsizing's do (22 Oct 2026). */
    const [todayFinancials, setTodayFinancials] = useState<Record<string, unknown> | null>(null);
    useEffect(() => {
        let cancelled = false;
        (calculateFinancials(factFind) as Promise<{ netWorth?: unknown }>)
            .then((r) => { if (!cancelled) { setTodayNetWorth(Number.isFinite(Number(r?.netWorth)) ? Number(r?.netWorth) : null); setTodayFinancials((r || null) as Record<string, unknown> | null); } })
            .catch(() => { if (!cancelled) { setTodayNetWorth(null); setTodayFinancials(null); } });
        return () => { cancelled = true; };
    }, [factFind]);
    const position: PositionSummary | null = useMemo(() => {
        const summary = years ? summarisePosition(years) : null;
        const today = todayYears?.[0];
        // Today's net worth: today's financials, else the run without the support cost (see above).
        if (!summary) return summary;
        if (todayNetWorth != null) return { ...summary, netWorth: todayNetWorth };
        return today ? { ...summary, netWorth: Number(today.netWorth) || 0 } : summary;
    }, [years, todayYears, todayNetWorth]);
    const first = years?.[0];
    /** The year the "today" figures are read from: the first year WITHOUT the support cost. */
    const todayFirst = todayYears?.[0] ?? first;

    /* ─── The tested plans ─── */
    const [strategies, setStrategies] = useState<CashflowPensionResult[] | null>(null);
    const [strategiesLoading, setStrategiesLoading] = useState(false);
    const [strategiesError, setStrategiesError] = useState<string | null>(null);
    /* 'manual' is the "Build a plan yourself" ledger, reached by a link (no mode chooser). */
    const [strategyMode, setStrategyMode] = useState<'manual' | 'optimised'>(() => (reopened?.mode === 'manual' ? 'manual' : 'optimised'));
    const [allocation, setAllocation] = useState<ManualAllocation>(() => reopened?.allocation ?? EMPTY_ALLOCATION);
    /* The annuity's TERMS, held beside the amount (allocationTotal sums the allocation map). */
    const [annuityTerms, setAnnuityTerms] = useState<ManualAnnuityTerms>(() => reopened?.annuityTerms ?? DEFAULT_ANNUITY_TERMS);
    /*
     * A QUOTE BELONGS TO ONE TYPE AND ONE AMOUNT (owner, 21 Oct 2026; annuityQuote.ts): the type and amount of
     * the plan it was entered on. Plans of the same type use it scaled to their own amount; the others keep our
     * estimate. The draft being typed is kept apart, so a digit does not fire a run.
     */
    const [seAnnuityQuote, setSeAnnuityQuote] = useState<AnnuityQuote | null>(() => (reopened?.annuityQuote && reopened.annuityQuoteFor
        ? { income: reopened.annuityQuote, amount: reopened.annuityQuoteFor.amount, shape: reopened.annuityQuoteFor.shape }
        : null));
    const [seAnnuityQuoteDraft, setSeAnnuityQuoteDraft] = useState<number | string>(() => reopened?.annuityQuote ?? '');
    const [manualResult, setManualResult] = useState<CashflowPensionResult[] | null>(null);
    const [manualLoading, setManualLoading] = useState(false);
    /* The plans themselves, kept so a choice records exactly what was run (the Action Plan rebuilds from them). */
    const [manualCandidate, setManualCandidate] = useState<unknown>(null);
    const [engineCandidates, setEngineCandidates] = useState<CashflowPensionCandidate[]>([]);
    const [manualStartedFrom, setManualStartedFrom] = useState<string | null>(null);
    /** The TESTED plan the hand-built one was copied from ("Customise this plan", 25 Oct 2026); null for a blank start. */
    const [manualCustomisedFrom, setManualCustomisedFrom] = useState<string | null>(null);

    const [saveNote, setSaveNote] = useState('');
    /** The tested plan chosen ON THESE INPUTS — it lights its card; a change of inputs lets it go. */
    const [selectedEngineId, setSelectedEngineId] = useState<string | null>(() => (reopened?.mode === 'manual' ? null : reopened?.engineOptionId ?? null));
    /** A line saying the chosen plan is no longer offered, or could not be worked out. */
    const [reopenNote, setReopenNote] = useState<string | null>(null);
    const [explain, setExplain] = useState<{ kind: 'row'; row: CashflowPensionResult } | { kind: 'manual' } | { kind: 'card' } | null>(null);
    const [risk, setRisk] = useState<{ name: string; kinds: RiskKind[]; horizonWarning?: string | null } | null>(null);
    /** An action asked for on the plan page that needs the plans re-run first. */
    const [pending, setPending] = useState<'advice' | 'quote' | 'yearByYear' | null>(null);
    /** A plan's year-by-year figures, opened from its Explain this. */
    const [yearByYear, setYearByYear] = useState<{ title: string; projections: unknown[] } | null>(null);

    const sources = useMemo(() => liquidSourcesOf(factFind), [factFind]);
    // The debts Manual Control can actually repay — the same list the payoff is built from.
    const totalDebt = useMemo(() => payableDebtsForCashflow(factFind).reduce((sum, d) => sum + d.balance, 0), [factFind]);
    const reserve = cashflowReserveOf(factFind);

    /*
     * Re-rank when the quote is committed. Only when plans are already showing: before the client has asked
     * for them there is nothing to re-rank.
     */
    useEffect(() => {
        if (strategyMode !== 'optimised') return;
        if (!strategies && !strategiesLoading) return;
        loadStrategies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seAnnuityQuote]);

    /*
     * Each run is numbered, and only the latest may land. Without this a run started before the support cost
     * changed could finish after the change and put the old plans back on screen.
     */
    const runSeq = useRef(0);
    const loadStrategies = async () => {
        const seq = ++runSeq.current;
        setStrategiesLoading(true);
        setStrategiesError(null);
        try {
            const candidates = generateCashflowPensionCandidates(factFind, seAnnuityQuote);
            const results = await evaluateCashflowPensionCandidates(
                factFind,
                candidates,
                legacyYears,
                supportCost?.annual || 0,
            ) as CashflowPensionResult[];
            if (seq !== runSeq.current) return;
            setEngineCandidates(candidates);
            setStrategies(results || []);
        } catch (e) {
            if (seq !== runSeq.current) return;
            setStrategiesError(e instanceof Error ? e.message : 'We couldn’t model the plans just now.');
        } finally {
            if (seq === runSeq.current) setStrategiesLoading(false);
        }
    };

    /*
     * A manual allocation is turned into the SAME candidate shape the engine ranks, and evaluated by the same
     * call. Two code paths to one answer is how a module comes to show a client two numbers for one plan.
     */
    useEffect(() => {
        if (strategyMode !== 'manual') return undefined;
        const allocated = allocationTotal(allocation);
        // Nothing to run: not working either (a run cancelled mid-flight must not leave the panel on "…").
        if (allocated <= 0) { setManualResult(null); setManualLoading(false); return undefined; }

        let cancelled = false;
        setManualLoading(true);
        const timer = setTimeout(() => {
            // The file too, so "Off your debt" becomes a real payoff against its liabilities.
            const candidate = manualCandidateFrom(allocation, annuityTerms, sources, factFind);
            setManualCandidate(candidate);
            evaluateCashflowPensionCandidates(
                factFind,
                [{ id: 'cp-nothing', name: 'Change nothing', description: '', changeNothing: true }, candidate],
                legacyYears,
                supportCost?.annual || 0,
            )
                .then((r: unknown) => { if (!cancelled) setManualResult((r || []) as CashflowPensionResult[]); })
                .catch(() => { if (!cancelled) setManualResult(null); })
                .finally(() => { if (!cancelled) setManualLoading(false); });
        }, 500); // typing settles before a full projection is run

        return () => { cancelled = true; clearTimeout(timer); };
    }, [strategyMode, allocation, annuityTerms, sources, factFind, legacyYears, supportCost]);

    /*
     * Any change to the inputs invalidates a ranking built on the old ones — AND THE CHOICE MADE ON IT (review,
     * 18 Oct 2026: changing the support cost cleared the options but kept the selection). Compared against the
     * last inputs, so opening the page does not count as a change. The client's income-only confirmation is
     * not an input to any figure, so ticking it does not re-test the plans under the reader.
     */
    const calcKey = useMemo(() => {
        const goals = { ...((factFind?.goals || {}) as Record<string, unknown>) };
        delete goals.incomeOnlyConfirmation;
        delete goals.incomeOnlyUnderstood;
        return JSON.stringify({ ...factFind, goals });
    }, [factFind]);
    const lastInputs = useRef<{ calcKey: string; legacyYears: number; supportAnnual: number } | null>(null);
    useEffect(() => {
        const now = { calcKey, legacyYears, supportAnnual: supportCost?.annual ?? 0 };
        const before = lastInputs.current;
        lastInputs.current = now;
        if (!before) return;
        if (before.calcKey === now.calcKey && before.legacyYears === now.legacyYears && before.supportAnnual === now.supportAnnual) return;
        runSeq.current += 1; // a run in flight was started on the old inputs
        setStrategiesLoading(false);
        setStrategiesError(null);
        setStrategies(null);
        setSelectedEngineId(null);
        setReopenNote(null);
        setSaveNote('');
    }, [calcKey, legacyYears, supportCost]);

    /* Step 3 runs the plans on its own once the questions are answered, and again after any change of input. */
    useEffect(() => {
        if (view !== 'steps' || step !== 3 || strategyMode !== 'optimised' || asking) return;
        if (strategies || strategiesLoading || strategiesError) return;
        void loadStrategies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, step, strategyMode, asking, strategies, strategiesLoading, strategiesError]);

    /*
     * The chosen plan is checked against every new run: one no longer offered, or that the engine could not
     * work out this time, is let go with a plain line saying why. A chosen plan priced on the same inputs
     * lights its card again.
     */
    useEffect(() => {
        if (!strategies) return;
        if (!selectedEngineId && chosen && !chosen.manual && chosen.engineId && !supportChangedSince(chosen.card, supportCost?.annual ?? 0) && chosen.years === legacyYears) {
            const again = strategies.find((s) => s.id === chosen.engineId && !s.error);
            if (again) { setSelectedEngineId(again.id); return; }
        }
        if (!selectedEngineId) return;
        const match = strategies.find((s) => s.id === selectedEngineId);
        if (match && !match.error) return;
        setSelectedEngineId(null);
        setReopenNote(match?.error
            ? 'The plan you had chosen couldn’t be worked out on your current figures. It stays chosen until you choose again.'
            : 'The plan you had chosen is no longer among those tested on your current figures. It stays chosen until you choose again.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [strategies, selectedEngineId]);

    /* ─── Reading a plan ─── */
    const candidateOf = (row: { id?: string } | null | undefined): CashflowPensionCandidate | null => engineCandidates.find((c) => c.id === row?.id) ?? null;
    /*
     * THE GUARANTEED INCOME IN A TESTED PLAN (owner, 21 Oct 2026). Read from the candidate as run, so the panel,
     * the confirmation, the quote and the document describe the same product. Every such plan buys it in the
     * client's name and does nothing else, so changing nothing IS the same plan without it.
     */
    const optionAnnuityOf = (option: { id?: string } | null | undefined) => {
        const a = candidateOf(option)?.annuity as { purchaseAmount?: unknown; annuityType?: unknown; residualCapital?: unknown } | undefined;
        const amount = Number(a?.purchaseAmount) || 0;
        if (!a || !(amount > 0)) return null;
        const residualCapital = Number(a.residualCapital) || 0;
        return {
            amount,
            annuityType: String(a.annuityType || 'lifetime'),
            residualCapital,
            kind: familyPanelKindOf({ annuityType: a.annuityType, residualCapital }),
            incomeOnly: isIncomeOnlyAnnuity({ purchaseAmount: amount, annuityType: a.annuityType, residualCapital }),
        };
    };
    /*
     * INCOME ONLY NEEDS THE CLIENT'S TICK (owner, 21 Oct 2026): a plan whose guaranteed income returns nothing
     * cannot be chosen (and so saved, or advised on) until the client has confirmed it for that amount. Held only
     * where the tick can be given (App passes the writer).
     */
    const setIncomeOnlyUnderstood = onIncomeOnlyUnderstoodChange;
    const engineIncomeOnlyHeld = (option: CashflowPensionResult) => {
        const optionAnnuity = optionAnnuityOf(option);
        return !!setIncomeOnlyUnderstood && incomeOnlyHeld(factFind, !!optionAnnuity?.incomeOnly, optionAnnuity?.amount ?? 0);
    };
    const manualIncomeOnly = strategyMode === 'manual' && allocation.annuity >= MIN_ANNUITY_PURCHASE && Math.round(annuityTerms.residualCapital * 100) <= 0 && annuityTerms.annuityType !== 'guaranteed';
    const manualIncomeOnlyHeld = !!setIncomeOnlyUnderstood && incomeOnlyHeld(factFind, manualIncomeOnly, Number(allocation?.annuity) || 0);
    const clientName = factFind?.client?.firstName || (v.you ? 'you' : 'the client');
    const nothingOf = (list: CashflowPensionResult[] | null | undefined) => list?.find((s) => s.changeNothing && !s.error) ?? null;
    const changeNothingRun = nothingOf(strategies);
    /*
     * THE CHOSEN PLAN'S FIGURES FOLLOW ITS RE-TEST (owner, 25 Oct 2026: as aged care and Support at home). Tested
     * again over the same years and the same support cost, the chosen tested plan is brought up to date on its record
     * and on the plan page, or marked out of date where the run no longer has it.
     */
    useEffect(() => {
        if (!chosen || chosen.manual || !chosen.engineId || !strategies || strategiesLoading || !setSavedStrategies) return;
        if (Number(chosen.years) !== Number(legacyYears) || Math.round(Number(chosen.card.support?.annual) || 0) !== Math.round(Number(supportCost?.annual) || 0)) return;
        const row = strategies.find((s) => s.id === chosen.engineId && !s.error) ?? null;
        const base = changeNothingRun;
        const plan = row ? { legacy: Number(row.snapshot.estate) || 0, pension: Number(row.snapshot.pension) || 0, cashflow: Number(row.snapshot.cashflow) || 0 } : null;
        const baseline = base ? { legacy: Number(base.snapshot.estate) || 0, pension: Number(base.snapshot.pension) || 0, cashflow: Number(base.snapshot.cashflow) || 0 } : null;
        setSavedStrategies((prev) => {
            const next = plan
                ? retestChosenRecord((prev || []) as never, chosen.id, plan, baseline, profileFingerprint(factFind))
                : markRecordOutOfDate((prev || []) as never, chosen.id, NOT_FOUND_ON_RETEST);
            return (next as typeof prev | null) ?? prev;
        });
        if (plan && (Math.round(chosen.card.plan.legacy) !== Math.round(plan.legacy) || Math.round(chosen.card.plan.pension) !== Math.round(plan.pension) || Math.round(chosen.card.plan.cashflow) !== Math.round(plan.cashflow))) {
            setChosen((c) => (c ? { ...c, card: { ...c.card, plan, ...(baseline ? { baseline } : {}) } } : c));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [strategies, strategiesLoading]);
    const manualRow = manualResult?.find((r) => r.id === 'cp-manual') ?? null;
    const manualNothing = nothingOf(manualResult);

    /** What the family receives from a guaranteed income, against changing nothing (the same plan without it). */
    const familyFor = (option: CashflowPensionResult, compareRows: unknown, testId: string, personName = clientName) => {
        const optionAnnuity = optionAnnuityOf(option);
        if (option.error || !optionAnnuity) return null;
        return (
            <AnnuityFamilyPanel
                factFind={factFind}
                personName={personName}
                amount={optionAnnuity.amount}
                kind={optionAnnuity.kind}
                incomeAYear={Number((option.annuityTerms as { annualPayment?: unknown } | null | undefined)?.annualPayment) || null}
                rows={option.projections as never}
                compareRows={(compareRows || null) as never}
                compareNote={compareRows ? 'the same plan without it, the money left in savings' : null}
                horizonYears={legacyYears}
                testId={testId}
            />
        );
    };

    /* ─── Choosing ─── */
    const leaders = useMemo(() => cpLeadersOf(strategies, engineCandidates), [strategies, engineCandidates]);
    const soaLeaders = () => [
        ...(leaders.estate && leaders.estateBeats ? [{ label: 'Leaves the most', id: leaders.estate.id }] : []),
        ...(leaders.cashflow ? [{ label: 'Most left over each year', id: leaders.cashflow.id }] : []),
        ...(leaders.pension ? [{ label: 'Highest Age Pension', id: leaders.pension.id }] : []),
    ];

    /**
     * CHOOSING SAVES THE PLAN, like every module (strategy/chosenPlan.ts): one chosen cashflow plan per file,
     * its card on the record so the plan page shows exactly what was chosen. `type: 'cashflow_pension'` is its
     * OWN type, filed by what it is, not by the door the client came through.
     */
    const savePlan = (row: CashflowPensionResult | null, mode: 'manual' | 'optimised') => {
        const chosen = mode === 'manual' ? manualResult?.find((r) => r.id === 'cp-manual') : row;
        if (!chosen) {
            setSaveNote(mode === 'manual'
                ? 'Allocate something above before saving.'
                : 'Choose one of the options above before saving.');
            return;
        }
        // Nothing the engine could not work out is saved (review, 18 Oct 2026).
        if (chosen.error) {
            setSaveNote(mode === 'manual' ? MANUAL_PLAN_ERRORED : CANNOT_CHOOSE_ERRORED);
            return;
        }
        const held = mode === 'manual' ? manualIncomeOnlyHeld : engineIncomeOnlyHeld(chosen);
        if (held) return;
        const candidate = (mode === 'manual' ? manualCandidate : candidateOf(chosen)) as CpCandidateLike | null;
        const base = mode === 'manual' ? manualNothing : changeNothingRun;
        const words = cpPlanWords(candidate ?? { id: chosen.id }, factFind);
        const annuityIncome = Number((chosen.annuityTerms as { annualPayment?: unknown } | null | undefined)?.annualPayment) || null;
        const m = cpAmountsOf(candidate);
        const famAnnuity = mode === 'manual'
            ? (m.annuity > 0 ? { amount: m.annuity, kind: familyPanelKindOf({ annuityType: m.annuityType, residualCapital: m.residual }) } : null)
            : optionAnnuityOf(chosen);
        const family = famAnnuity ? (() => {
            const only = mode !== 'manual' || (m.funeralBond + m.gift + m.debt + m.superTotal + m.investmentBond <= 0);
            const built = buildSoaGuaranteedIncome({
                factFind, personName: clientName, amount: famAnnuity.amount, kind: famAnnuity.kind, incomeAYear: annuityIncome,
                rows: chosen.projections, compareRows: only ? (base?.projections ?? null) : null, compareNote: only && base ? 'the same plan without it, the money left in savings' : null,
                horizonYears: legacyYears, plan: { estate: 0, pension: 0, cashflow: 0 },
            });
            return built ? { personName: clientName, amount: famAnnuity.amount, kind: famAnnuity.kind, incomeAYear: annuityIncome, compareNote: built.compareNote, rows: built.rows } : null;
        })() : null;
        const plan = { legacy: chosen.snapshot.estate, pension: chosen.snapshot.pension, cashflow: chosen.snapshot.cashflow };
        const baseline = base ? { legacy: base.snapshot.estate, pension: base.snapshot.pension, cashflow: base.snapshot.cashflow } : null;
        const card: CpPlanCardRecord = {
            plan,
            baseline,
            words,
            horizonYears: legacyYears,
            riskKinds: cpKindsOf(candidate ?? { id: chosen.id }),
            pieces: cpPiecesOf(candidate),
            why: cpWhyLines({ estate: plan.legacy, pension: plan.pension, cashflow: plan.cashflow }, baseline ? { estate: baseline.legacy, pension: baseline.pension, cashflow: baseline.cashflow } : null, candidate, legacyYears, v, annuityIncome),
            support: { annual: supportCost?.annual ?? 0, label: supportCost?.label ?? supportLabel },
            annuity: m.annuity > 0 ? { amount: m.annuity, type: m.annuityType, residualPercent: Math.round(m.residual * 100) } : null,
            origin: mode === 'manual' ? 'manual' : 'engine',
            family,
            rows: compactStoryRows(chosen.projections as never),
            baseRows: base ? compactStoryRows(base.projections as never) : null,
        };
        const id = `cp-${Date.now()}`;
        const createdAt = new Date().toISOString();
        setSavedStrategies?.((prev) => withChosenPlan(prev as never, {
            id,
            name: words.title,
            displayName: words.title,
            type: 'cashflow_pension',
            // At the top level too: the Dashboard's source badge reads strategy.source.
            source: mode === 'manual' ? 'manual' : 'suggestion_engine',
            // The client's figures it was priced on, so Home can tell when they change (chosenPlanRefresh.ts).
            inputsFingerprint: profileFingerprint(factFind),
            // Built by the client from a tested plan ("Customise this plan", 25 Oct 2026).
            ...(mode === 'manual' && manualCustomisedFrom ? { customisedFrom: manualCustomisedFrom } : {}),
            inputs: {
                engineStrategyId: mode === 'manual' ? null : chosen.id,
                planContext: { supportCostAnnual: supportCost?.annual ?? 0, legacyYears },
                source: mode === 'manual' ? 'manual' : 'suggestion_engine',
                allocation: mode === 'manual' ? { ...allocation } : null,
                // The annuity's terms too, so a reopened hand-built plan is the same plan.
                annuityTerms: mode === 'manual' ? { ...annuityTerms } : null,
                engineOptionId: mode === 'manual' ? null : chosen.id,
                engineOptionName: mode === 'manual' ? null : chosen.name,
                // The plan as run, so its position can be rebuilt for the forms.
                candidate,
                supportCostAnnual: supportCost?.annual ?? 0,
                supportCostLabel: supportCost?.label ?? null,
                legacyYears,
                card,
            },
            outputs: {
                agePension: chosen.snapshot.pension,
                cashFlow: chosen.snapshot.cashflow,
                legacyEstimate: chosen.snapshot.estate,
            },
            createdAt,
        } as never) as never);
        setChosen({ card, createdAt, id, soaPreparedAt: null, engineId: mode === 'manual' ? null : chosen.id, manual: mode === 'manual', candidate, years: legacyYears, allocation: mode === 'manual' ? { ...allocation } as never : null, annuityTerms: mode === 'manual' ? { ...annuityTerms } as never : null, customisedFrom: mode === 'manual' ? manualCustomisedFrom : null });
        setSelectedEngineId(mode === 'manual' ? null : chosen.id);
        setReopenNote(null);
        setSaveNote('');
    };

    /** The Statement of Advice for a plan (cpSoa.ts) — opened, downloaded, registered, and marked on the plan. */
    const generateCpSoa = (option: CashflowPensionResult) => {
        // A plan the engine could not work out has no figures to advise on (review, 18 Oct 2026).
        if (option.error) return;
        if (engineIncomeOnlyHeld(option)) return;
        const leaders = soaLeaders();
        // The plan as run, so the document reads each lever's amount from its own field (cpSoa.ts).
        const candidate = engineCandidates.find((c) => c.id === option.id) ?? null;
        const snapshot = buildCashflowPensionSoaSnapshot({ option, all: strategies || [], leaders, factFind, legacyYears, supportCost, candidate, candidates: engineCandidates });
        openModuleSoa(snapshot);
        onSoaGenerated?.({ id: option.id, name: option.name, type: 'cashflow_pension', source: 'suggestion_engine', strategy: option, soaSnapshot: snapshot });
        markPrepared(option.id === chosen?.engineId && !chosen?.manual);
    };
    const generateManualSoa = () => {
        if (!manualRow || manualRow.error || manualIncomeOnlyHeld) return;
        const snapshot = buildCashflowPensionSoaSnapshot({ option: manualRow, all: manualResult || [], leaders: [], factFind, legacyYears, supportCost, candidate: manualCandidate as never, candidates: [manualCandidate as never], manual: true, customisedFrom: manualCustomisedFrom });
        openModuleSoa(snapshot);
        onSoaGenerated?.({ id: 'cp-manual', name: 'Your plan', type: 'cashflow_pension', source: 'manual', strategy: manualRow, soaSnapshot: snapshot });
        markPrepared(!!chosen?.manual);
    };
    const markPrepared = (isChosen: boolean) => {
        if (!isChosen || !chosen?.id) return;
        const at = new Date().toISOString();
        setChosen((c) => (c ? { ...c, soaPreparedAt: at } : c));
        setSavedStrategies?.((prev) => (prev || []).map((p) => (p?.id === chosen.id ? { ...p, soaPreparedAt: at } : p)) as never);
    };

    /* An action asked for on the plan page, once the plans it needs have run. */
    useEffect(() => {
        if (!pending || !chosen) return;
        if (supportChangedSince(chosen.card, supportCost?.annual ?? 0)) { setPending(null); return; }
        if (chosen.manual) {
            if (pending === 'advice' && manualRow && !manualLoading && !manualRow.error) { generateManualSoa(); setPending(null); }
            // The plan page's "See the year-by-year figures": the saved card keeps no rows, so they open once re-priced (22 Oct 2026).
            if (pending === 'yearByYear' && manualRow && !manualLoading && !manualRow.error) { setYearByYear({ title: chosen.card.words.title, projections: manualRow.projections }); setPending(null); }
            return;
        }
        if (!strategies) return;
        const row = strategies.find((s) => s.id === chosen.engineId);
        if (pending === 'advice' && row) generateCpSoa(row);
        if (pending === 'yearByYear' && row && !row.error) setYearByYear({ title: chosen.card.words.title, projections: row.projections });
        setPending(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pending, strategies, manualRow, manualLoading]);

    /* ─── Build a plan yourself ─── */
    /*
     * TWO DOORS (owner, 25 Oct 2026): "Customise this plan" (a tested plan) copies it in; "Build a plan yourself"
     * (none) opens a BLANK plan. It used to keep whatever amounts were last on screen.
     */
    const openBuild = (from: CpCandidateLike | null, label: string | null) => {
        if (from) {
            const seeded = allocationFromCandidate(from);
            setAllocation(seeded.allocation);
            setAnnuityTerms(seeded.terms);
        } else {
            setAllocation(EMPTY_ALLOCATION);
            setAnnuityTerms(DEFAULT_ANNUITY_TERMS);
        }
        setManualStartedFrom(label);
        setManualCustomisedFrom(from ? label : null);
        setStrategyMode('manual');
        setView('steps');
        setStep(3);
        try { window.scrollTo?.(0, 0); } catch { /* not in a browser */ }
    };
    /*
     * REOPENING A CHOSEN HAND-BUILT PLAN RESTORES ITS AMOUNTS (23 Oct 2026). "See the year-by-year figures" and "Get
     * the advice document" on the plan page re-price the plan; they opened the build page with whatever amounts were
     * on screen, which after a reload is nothing, so the plan was never priced and the request waited for good.
     */
    const openChosenBuild = (label: string) => {
        if (!chosen) return;
        if (chosen.allocation && Object.keys(chosen.allocation).length) {
            setAllocation({ ...EMPTY_ALLOCATION, ...(chosen.allocation as Record<string, unknown>) } as typeof EMPTY_ALLOCATION);
            if (chosen.annuityTerms) setAnnuityTerms({ ...DEFAULT_ANNUITY_TERMS, ...(chosen.annuityTerms as Record<string, unknown>) } as typeof DEFAULT_ANNUITY_TERMS);
            // Its own record says where it began, if it was customised from a tested plan.
            setManualStartedFrom(chosen.customisedFrom ?? label);
            setManualCustomisedFrom(chosen.customisedFrom ?? null);
            setStrategyMode('manual');
            setView('steps');
            setStep(3);
            return;
        }
        openBuild(chosen.candidate, label);
    };
    const startOptions = [
        ...[leaders.estate, leaders.cashflow, leaders.pension]
            .filter((r, i, all): r is CashflowPensionResult => !!r && all.findIndex((x) => x?.id === r.id) === i)
            .map((r) => ({ id: String(r.id), label: cpPlanWords(candidateOf(r) ?? { id: r.id }, factFind).title })),
        { id: 'blank', label: 'A blank plan' },
    ];

    /* ─── Explain this ─── */
    /*
     * EXPLAIN THIS AS AGED CARE TELLS IT (owner, 22 Oct 2026: "all modules use consistent UI"). Numbered lines —
     * what is done and paid from where, then each cause and what it does to the numbers (cpLedgerLines) — then
     * "Put together" with the money story inside it (only when a year runs short), then the "=" line against
     * changing nothing (CauseEffectLedger). The old three-figure grid is gone: the ledger states the same figures.
     * The guaranteed income's family panel, the plan's pieces and the debts follow, in that order.
     */
    const explainBody = (i: {
        words: { title: string; sub: string };
        plan: { estate: number; pension: number; cashflow: number };
        base: { estate: number; pension: number; cashflow: number } | null;
        candidate: CpCandidateLike | null;
        annuityIncome: number | null;
        rows: unknown[] | null;
        baseRows: unknown[] | null;
        pieces: ReturnType<typeof cpPiecesOf>;
        family: React.ReactNode;
        debts: React.ReactNode;
        /** The years the plan was priced on (a chosen card keeps its own). */
        years: number;
    }) => {
        const rows = i.rows;
        const story = rows && rows.length && summariseFunding(rows as never).hasDeficit
            ? buildMoneyStory({ rows: rows as never, factFind, runway: planRunwayCopy(rows as never, rows.length), deposit: { paid: 0, refundTo: '' }, horizonYears: i.years, enteringName: factFind?.client?.firstName || 'The household', spentOn: cpSpentOnWords(i.candidate) })
            : null;
        const causes = cpLedgerLines({ plan: i.plan, base: i.base, c: i.candidate, v, annuityIncome: i.annuityIncome, first: cpFirstYearOf(rows as never), baseFirst: cpFirstYearOf(i.baseRows as never) });
        const lines: React.ReactNode[] = [
            <span key="do" data-testid="cp-explain-do">{i.words.sub}</span>,
            ...causes.map((l) => <span key={l.key} data-testid={`cp-explain-${l.key}`}><strong className="text-slate-900">{l.lead}</strong><ChangeOrText change={l.change} text={l.body} /></span>),
            putTogetherLine({
                cashflow: i.plan.cashflow,
                baseCashflow: i.base?.cashflow ?? null,
                they: v.they,
                baseWord: 'changing nothing',
                moneyStory: story ? <MoneyStoryBlock story={story} testId="cp-explain-money-story" /> : null,
            }),
        ];
        return (
            <div data-testid="cp-explain">
                <CauseEffectLedger lines={lines} estate={i.plan.estate} baseEstate={i.base?.estate ?? null} horizonYears={i.years} baseWord="changing nothing" testId="cp-cause-effect" leverKinds={cpKindsOf(i.candidate)}>
                    {i.family && <div className="mt-5">{i.family}</div>}
                    <PlanPiecesCard className="mt-5" pieces={i.pieces} />
                    {i.debts}
                </CauseEffectLedger>
            </div>
        );
    };

    /* ─── Rendering ─── */
    const backButton = (
        /* Back returns through the door used (review, 18 Oct 2026: it always went to Support at home). */
        <button type="button" onClick={() => setActivePage(cameFrom === 'support_at_home' ? 'HomeCarePage' : 'Dashboard')} className="mb-3 -ml-1 inline-flex items-center gap-1 text-[14px] font-semibold text-slate-600 hover:text-slate-900">
            <ChevronLeft size={16} /> {cameFrom === 'support_at_home' ? 'Back to Support at home' : 'Back to Home'}
        </button>
    );

    /*
     * Every "What could go wrong" carries the age-and-health warning when the plan buys a guaranteed income
     * (owner, 22 Oct 2026: the chosen plan's never did) — read from the plan as run (cpHorizonWarningOf).
     */
    const riskPanel = risk && (
        <StrategyRisksPanel voice={v} strategyName={risk.name} onClose={() => setRisk(null)} risks={buildStrategyRisks(risk.kinds, { horizonWarning: risk.horizonWarning ?? null })} />
    );

    /* Explain this, as aged care says it (owner, 22 Oct 2026): an intro saying how to read it and what the benchmark is. */
    const explainIntro = `Each line says what ${v.they} do, and what that does to the numbers. "Changing nothing" means keeping everything as it is today${supportCost ? ', with the same support paid for' : ''}, over the same years.`;
    const yearByYearButton = (onClick: () => void) => (
        <button type="button" onClick={onClick} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-[14px] font-semibold text-slate-800 hover:border-blue-300 hover:bg-blue-50" data-testid="cp-explain-year-by-year">See the year-by-year figures</button>
    );

    const explainModal = (() => {
        if (!explain) return null;
        if (explain.kind === 'card' && chosen) {
            const c = chosen.card;
            return (
                <PlanExplanationModal
                    eyebrow="Explain this"
                    title={c.words.title}
                    intro={explainIntro}
                    onClose={() => setExplain(null)}
                    // The saved card keeps no projection rows, so the plans are re-priced and the figures open for this plan (as SahHub).
                    footer={yearByYearButton(() => {
                        setExplain(null);
                        setPending('yearByYear');
                        if (chosen.manual) openChosenBuild(c.words.title);
                        else { setStrategyMode('optimised'); setAsking(false); goToStep(3); }
                    })}
                >
                    {explainBody({
                        words: c.words,
                        plan: { estate: c.plan.legacy, pension: c.plan.pension, cashflow: c.plan.cashflow },
                        base: c.baseline ? { estate: c.baseline.legacy, pension: c.baseline.pension, cashflow: c.baseline.cashflow } : null,
                        candidate: chosen.candidate,
                        annuityIncome: c.family?.incomeAYear ?? null,
                        // The rows saved with the card (older cards have none, so the shorter ledger shows).
                        rows: (c.rows as unknown[] | null | undefined) ?? null,
                        baseRows: (c.baseRows as unknown[] | null | undefined) ?? null,
                        pieces: c.pieces,
                        family: c.family && c.family.rows.length > 0 ? (
                            <AnnuityFamilyPanel factFind={factFind} personName={c.family.personName} amount={c.family.amount} kind={c.family.kind} incomeAYear={c.family.incomeAYear} rows={null} frozen={c.family.rows} compareNote={c.family.compareNote} horizonYears={c.horizonYears} testId="cp-explain-family-panel" />
                        ) : null,
                        debts: <DebtsInPlanNote factFind={factFind} clearedAmount={cpDebtClearedBy(chosen.candidate)} years={c.horizonYears} context={String(chosen.candidate?.id || '').startsWith('cp-your-plan') || c.origin === 'manual' ? 'manual' : 'chosen'} />,
                        years: c.horizonYears,
                    })}
                </PlanExplanationModal>
            );
        }
        const isManual = explain.kind === 'manual';
        const row = isManual ? manualRow : explain.kind === 'row' ? explain.row : null;
        if (!row) return null;
        const candidate = (isManual ? manualCandidate : candidateOf(row)) as CpCandidateLike | null;
        const base = isManual ? manualNothing : changeNothingRun;
        const words = cpPlanWords(candidate ?? { id: row.id }, factFind);
        const plan = { estate: row.snapshot.estate, pension: row.snapshot.pension, cashflow: row.snapshot.cashflow };
        const baseFig = base ? { estate: base.snapshot.estate, pension: base.snapshot.pension, cashflow: base.snapshot.cashflow } : null;
        const income = Number((row.annuityTerms as { annualPayment?: unknown } | null | undefined)?.annualPayment) || null;
        return (
            <PlanExplanationModal
                eyebrow="Explain this"
                title={words.title}
                intro={explainIntro}
                onClose={() => setExplain(null)}
                footer={row.projections?.length ? yearByYearButton(() => { setExplain(null); setYearByYear({ title: words.title, projections: row.projections }); }) : undefined}
            >
                {explainBody({
                    words,
                    plan,
                    base: baseFig,
                    candidate,
                    annuityIncome: income,
                    rows: row.projections,
                    baseRows: base?.projections ?? null,
                    pieces: cpPiecesOf(candidate),
                    // What the family receives from a guaranteed income, against the same plan without it (a tested plan's).
                    family: isManual ? null : familyFor(row, changeNothingRun?.projections, 'cp-explain-family-panel'),
                    debts: (() => {
                        // The same plan with the debts paid off ("<id>-clear-debt", cashflowPensionCandidates.clearDebtTwins).
                        const twin = isManual ? null : (strategies || []).find((s) => s.id === `${row.id}-clear-debt`) ?? (candidate?.changeNothing ? (strategies || []).find((s) => s.id === 'cp-clear-debt') : null) ?? null;
                        return <DebtsInPlanNote factFind={factFind} clearedAmount={cpDebtClearedBy(candidate)} plan={plan} twin={twin ? { estate: twin.snapshot.estate, pension: twin.snapshot.pension, cashflow: twin.snapshot.cashflow } : null} years={legacyYears} context={isManual ? 'manual' : 'tested'} />;
                    })(),
                    years: legacyYears,
                })}
            </PlanExplanationModal>
        );
    })();

    /* The year-by-year figures, opened from Explain this — the one projections view every module shares. */
    const yearByYearModal = yearByYear && (
        <ProjectionsModal onClose={() => setYearByYear(null)} title={yearByYear.title} comparedOverYears={Number(legacyYears) || undefined} projections={yearByYear.projections as never} factFind={factFind as never} initialFinancials={todayFinancials as never} costLabel={null} />
    );

    if (view === 'hub' && chosen) {
        const card = chosen.card;
        return (
            <div className="mx-auto max-w-6xl">
                <CpHub
                    factFind={factFind}
                    card={card}
                    outOfDate={chosenPlanOutOfDate(chosenCpPlanOf(savedStrategies) ?? (chosen.id ? (savedStrategies || []).find((p) => (p as { id?: unknown }).id === chosen.id) : null), factFind)}
                    candidate={chosen.candidate}
                    chosenOn={chosen.createdAt}
                    soaPreparedAt={chosen.soaPreparedAt}
                    back={cameFrom === 'support_at_home' ? { label: 'Back to Support at home', onClick: () => setActivePage('HomeCarePage') } : null}
                    onChangeSupport={() => goToStep(1)}
                    onExplain={() => setExplain({ kind: 'card' })}
                    onRisks={() => setRisk({ name: card.words.title, kinds: card.riskKinds, horizonWarning: cpHorizonWarningOf(chosen.candidate, factFind, card.annuity) })}
                    onAdvice={() => {
                        setPending('advice');
                        if (chosen.manual) openChosenBuild(card.words.title);
                        else { setStrategyMode('optimised'); setAsking(false); goToStep(3); }
                    }}
                    onAllPlans={() => { setStrategyMode('optimised'); setAsking(false); goToStep(3); }}
                    onBuild={() => (chosen.manual ? openChosenBuild(card.words.title) : openBuild(chosen.candidate, card.words.title))}
                    onBuildNew={() => openBuild(null, null)}
                    onQuote={() => { setPending('quote'); setStrategyMode('optimised'); setAsking(false); goToStep(3); }}
                    onAssumptions={() => setActivePage('AssumptionsPage')}
                    onHome={() => setActivePage('Dashboard')}
                    /*
                     * Model something else (owner, 22 Oct 2026): cashflow keeps one plan per file, so a new model
                     * starts at step 1 with nothing carried over and the questions asked again, as Home's "Start a
                     * new model" does; the chosen plan stays chosen until another is chosen.
                     */
                    onStartFresh={() => {
                        setSupportAnnual('');
                        setSupportLabel('Support at home services');
                        setLegacyYears(10);
                        setSelectedEngineId(null);
                        setManualStartedFrom(null);
                        setStrategyMode('optimised');
                        setAsking(true);
                        goToStep(1);
                    }}
                    /* From Support at home: change the support paid for first, keeping the answers to the questions. */
                    onChangeSupportFirst={cameFrom === 'support_at_home' ? () => { setStrategyMode('optimised'); setAsking(false); goToStep(1); } : null}
                />
                {riskPanel}
                {explainModal}
                {yearByYearModal}
            </div>
        );
    }

    const supportChanged = !!chosen && supportChangedSince(chosen.card, supportCost?.annual ?? 0);
    const chosenHere = !!chosen && !chosen.manual && selectedEngineId != null && selectedEngineId === chosen.engineId;

    return (
        <div className="mx-auto max-w-6xl">
            {backButton}
            <p className="text-[13px] text-slate-500">
                <button type="button" onClick={() => setActivePage('Dashboard')} className="underline decoration-dotted underline-offset-2 hover:text-slate-800">Home</button>
                {' › Cashflow & Age Pension › '}{step === 1 ? 'Your money today' : step === 2 ? 'What it means' : strategyMode === 'manual' ? 'Build a plan yourself' : 'Ways to improve it'}
                {chosen && <> · <button type="button" onClick={() => setView('hub')} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4" data-testid="cp-go-to-plan">{v.you ? 'Your' : v.Possessive} money plan</button></>}
            </p>
            <h1 className="mt-2 text-[32px] font-bold leading-tight tracking-tight text-brand-navy" data-testid="cp-title">{v.you ? 'Your money, as it stands' : `${v.Possessive} money, as it stands`}</h1>
            <p className="mt-1 text-[15px] text-slate-600" data-testid="cashflow-intro">
                {cameFrom === 'support_at_home'
                    ? `None of this needs a care assessment. Three short steps: where ${v.names} stand${v.s} today, why the numbers are what they are, and ways to improve them without moving house.`
                    : `Three short steps: where ${v.names} stand${v.s} today, why the numbers are what they are, and ways to improve them without moving house.`}
            </p>
            <AnalyserSteps
                step={step}
                onStep={goToStep}
                ariaLabel="Cashflow and Age Pension steps"
                steps={[
                    { title: `${v.you ? 'Your' : v.Possessive} money today`, sub: supportCost ? `With ${money(supportCost.annual)} a year of support` : 'Four numbers, with any support paid for' },
                    { title: 'What it means', sub: 'Why the Age Pension is what it is' },
                    { title: 'Ways to improve it', sub: 'Plans we tested, or build your own' },
                ]}
            />

            {step === 1 && (
                <div className="mt-8">
                    <CpStep1
                        factFind={factFind}
                        supportAnnual={supportAnnual}
                        onSupportAnnual={setSupportAnnual}
                        supportLabel={supportLabel}
                        onSupportLabel={setSupportLabel}
                        supportCost={supportCost}
                        position={position}
                        loading={loading}
                        error={error}
                        legacyYears={legacyYears}
                        onLegacyYears={setLegacyYears}
                        chosenCard={chosen?.card ?? null}
                        onWhy={() => goToStep(2)}
                        onNext={() => goToStep(2)}
                    />
                </div>
            )}

            {step === 2 && (
                <div className="mt-8">
                    <CpStep2
                        factFind={factFind}
                        first={first}
                        todayFirst={todayFirst}
                        years={years}
                        position={position}
                        supportCost={supportCost}
                        legacyYears={legacyYears}
                        onBack={() => goToStep(1)}
                        onNext={() => { setStrategyMode('optimised'); goToStep(3); }}
                        onAssumptions={() => setActivePage('AssumptionsPage')}
                    />
                </div>
            )}

            {step === 3 && strategyMode === 'optimised' && (
                <div className="mt-8">
                    {asking ? (
                        <CpQuestions
                            factFind={factFind}
                            onGuaranteedIncomeGoals={onGuaranteedIncomeGoals}
                            legacyYears={legacyYears}
                            firstRun={!strategies}
                            onRun={(a: CpAnswers) => {
                                const goals = (factFind?.goals || {}) as { cpEmergencyReserve?: unknown; cpLevers?: unknown };
                                const levers = CASHFLOW_LEVERS.filter((l) => a.levers.includes(l)).join(',');
                                if (onCashflowGoals && (Number(goals.cpEmergencyReserve) !== a.reserve || goals.cpEmergencyReserve == null || goals.cpLevers !== levers)) {
                                    onCashflowGoals({ cpEmergencyReserve: a.reserve, cpLevers: levers });
                                }
                                setLegacyYears(a.years);
                                setAsking(false);
                            }}
                            onBuildYourOwn={() => { setAsking(false); openBuild(null, null); }}
                            onCancel={() => setAsking(false)}
                        />
                    ) : strategiesLoading || (!strategies && !strategiesError) ? (
                        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-card" data-testid="cp-testing">
                            <p className="text-[16px] font-semibold text-slate-900">Testing the ways to improve it</p>
                            <p className="mt-1 text-[14px] text-slate-600">Each way, and changing nothing, over the same {legacyYears} years.</p>
                        </div>
                    ) : strategiesError ? (
                        <div className="rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] text-slate-700">
                            {strategiesError} <button type="button" onClick={() => void loadStrategies()} className="font-semibold text-brand-navy underline">Try again</button> or <button type="button" onClick={() => openBuild(null, null)} className="font-semibold text-brand-navy underline">build a plan yourself</button>.
                        </div>
                    ) : (
                        <CpPlansView
                            notices={(
                                <>
                                    {chosenHere && chosen && (
                                        <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 border-l-4 border-l-emerald-500 bg-white px-5 py-4 shadow-card" data-testid="cp-chosen-banner">
                                            <span className="text-[26px] leading-none text-emerald-500" aria-hidden>✓</span>
                                            <p className="min-w-0 flex-1 text-[14px] leading-relaxed text-slate-700">
                                                <strong className="text-slate-900">{v.you ? "You've" : `${v.Names} ${v.have}`} chosen &ldquo;{chosen.card.words.title}&rdquo;.</strong>{' '}
                                                It&apos;s saved with {v.possessive} file. The next step is the advice document{chosen.card.annuity ? ', and a quote from the provider' : ''}.
                                            </p>
                                            <button type="button" onClick={() => setView('hub')} className="text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4" data-testid="cp-banner-plan-page">Go to {v.possessive} money plan</button>
                                        </div>
                                    )}
                                    {chosen && !chosenHere && (supportChanged || chosen.manual || reopenNote) && (
                                        <p className="rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700" data-testid="cp-retested-note">
                                            {supportChanged
                                                ? <>Re-tested with {money(supportCost?.annual ?? 0)} a year of support. {v.Possessive} chosen plan, &ldquo;{chosen.card.words.title}&rdquo;, was priced on {money(chosen.card.support.annual)} and stays chosen until {v.they} choose again.</>
                                                : chosen.manual ? <>{v.Possessive} chosen plan is one {v.you ? 'you' : v.they} built: &ldquo;{chosen.card.words.title}&rdquo;. It stays chosen until {v.they} choose another.</>
                                                    : reopenNote}
                                        </p>
                                    )}
                                    {saveNote && <p className="text-[14px] text-slate-700" role="status">{saveNote}</p>}
                                </>
                            )}
                            factFind={factFind}
                            results={strategies || []}
                            candidates={engineCandidates}
                            horizonYears={legacyYears}
                            ownToday={position?.netWorth ?? 0}
                            supportCost={supportCost}
                            focus={focus}
                            onFocus={setFocus}
                            chosenId={selectedEngineId}
                            initialOpenDetail={pending === 'quote' ? chosen?.engineId ?? null : null}
                            onChoose={(row) => savePlan(row, 'optimised')}
                            onExplain={(row) => setExplain({ kind: 'row', row })}
                            onRisks={(row) => setRisk({ name: cpPlanWords(candidateOf(row) ?? { id: row.id }, factFind).title, kinds: cpKindsOf(candidateOf(row) ?? { id: row.id }), horizonWarning: cpHorizonWarningOf(candidateOf(row), factFind) })}
                            onAdvice={(row) => generateCpSoa(row)}
                            onBuildYourOwn={(row) => openBuild(row ? candidateOf(row) : null, row ? cpPlanWords(candidateOf(row) ?? { id: row.id }, factFind).title : null)}
                            onChanges={() => setAsking(true)}
                            renderFamily={(option) => familyFor(option, changeNothingRun?.projections, `cp-annuity-family-panel-${option.id}`)}
                            beforeChoose={(option) => {
                                const optionAnnuity = optionAnnuityOf(option);
                                return optionAnnuity?.incomeOnly && setIncomeOnlyUnderstood
                                    ? <IncomeOnlyBeforeChoose factFind={factFind} amount={optionAnnuity.amount} onChange={setIncomeOnlyUnderstood} />
                                    : undefined;
                            }}
                            chooseHeld={engineIncomeOnlyHeld}
                            renderDetail={(option) => {
                                const optionAnnuity = optionAnnuityOf(option);
                                const c = candidateOf(option);
                                return (
                                    <div className="space-y-3 text-[13px] leading-relaxed text-slate-700">
                                        {c?.fundingSources?.length ? <p><strong className="text-slate-900">Where the money comes from:</strong> {cpPlanWords(c, factFind).sub}</p> : null}
                                        <p className="text-slate-500">Kept back for emergencies: {money(reserve)}, drawn on only if a year runs short.</p>
                                        {optionAnnuity && (
                                            <EngineAnnuityTerms
                                                terms={option.annuityTerms}
                                                factFind={factFind}
                                                /*
                                                 * A REAL QUOTE, and the whole field re-ranked on it: a different rate can change
                                                 * which plan wins (owner, 2026-09-01). The quote is for THIS plan's type and
                                                 * amount (annuityQuote.ts).
                                                 */
                                                quote={<>
                                                    <AnnuityQuoteControl
                                                        draft={seAnnuityQuoteDraft}
                                                        onDraft={setSeAnnuityQuoteDraft}
                                                        committed={seAnnuityQuote?.income ?? null}
                                                        onCommit={(next) => setSeAnnuityQuote(next != null && next > 0
                                                            ? { income: next, amount: optionAnnuity.amount, shape: annuityQuoteShapeOf(optionAnnuity.annuityType, Math.round(optionAnnuity.residualCapital * 100)) }
                                                            : null)}
                                                    />
                                                    {annuityQuoteNote(seAnnuityQuote, formatCurrency) && (
                                                        <p className="mt-1.5 text-[12px] text-slate-500" data-testid="cp-annuity-quote-note">{annuityQuoteNote(seAnnuityQuote, formatCurrency)}</p>
                                                    )}
                                                    <p className="mt-1.5 text-[12px] text-slate-500">These incomes are estimates from published rates — get a quote from the provider.</p>
                                                </>}
                                            />
                                        )}
                                    </div>
                                );
                            }}
                        />
                    )}
                </div>
            )}

            {step === 3 && strategyMode === 'manual' && (
                <div className="mt-8">
                    <CpBuildPlan
                        factFind={factFind}
                        sources={sources}
                        reserve={reserve}
                        allocation={allocation}
                        onAllocation={setAllocation}
                        annuityTerms={annuityTerms}
                        onAnnuityTerms={setAnnuityTerms}
                        totalDebt={totalDebt}
                        onIncomeOnlyUnderstoodChange={setIncomeOnlyUnderstood}
                        horizonYears={legacyYears}
                        working={manualLoading}
                        erroredWords={manualRow?.error ? MANUAL_PLAN_ERRORED : null}
                        effect={{
                            cashflow: manualRow && !manualRow.error ? manualRow.snapshot.cashflow : null,
                            baseCashflow: manualNothing ? manualNothing.snapshot.cashflow : null,
                            pension: manualRow && !manualRow.error ? manualRow.snapshot.pension : null,
                            basePension: manualNothing ? manualNothing.snapshot.pension : null,
                            legacy: manualRow && !manualRow.error ? manualRow.snapshot.estate : null,
                            baseLegacy: manualNothing ? manualNothing.snapshot.estate : null,
                        }}
                        startedFrom={manualStartedFrom}
                        customising={!!manualCustomisedFrom}
                        onClear={() => { setAllocation(EMPTY_ALLOCATION); setAnnuityTerms(DEFAULT_ANNUITY_TERMS); setManualStartedFrom(null); setManualCustomisedFrom(null); }}
                        startOptions={startOptions}
                        onStartFrom={(id) => {
                            if (id === 'blank') { setAllocation(EMPTY_ALLOCATION); setAnnuityTerms(DEFAULT_ANNUITY_TERMS); setManualStartedFrom('A blank plan'); setManualCustomisedFrom(null); return; }
                            const row = strategies?.find((s) => String(s.id) === id);
                            if (row) openBuild(candidateOf(row), cpPlanWords(candidateOf(row) ?? { id: row.id }, factFind).title);
                        }}
                        onExplain={() => setExplain({ kind: 'manual' })}
                        /* The plan's own title, as its Explain this says it (owner, 22 Oct 2026: it said "Your own plan" here). */
                        onRisks={() => setRisk({ name: cpPlanWords((manualCandidate as CpCandidateLike | null) ?? { id: 'cp-manual' }, factFind).title, kinds: cpKindsOf(manualCandidate as CpCandidateLike | null), horizonWarning: cpHorizonWarningOf(manualCandidate as CpCandidateLike | null, factFind) })}
                        onChoose={() => savePlan(null, 'manual')}
                        chosen={!!chosen?.manual && !!manualRow && !manualLoading && chosen.card.plan.legacy === manualRow.snapshot.estate && chosen.card.plan.cashflow === manualRow.snapshot.cashflow}
                        beforeChoose={manualIncomeOnly && setIncomeOnlyUnderstood
                            ? <IncomeOnlyBeforeChoose factFind={factFind} amount={allocation.annuity} onChange={setIncomeOnlyUnderstood} action="saving" />
                            : undefined}
                        chooseHeld={manualIncomeOnlyHeld}
                        onBackToPlans={() => { setStrategyMode('optimised'); goToStep(3); }}
                        onHome={() => setActivePage('Dashboard')}
                        onAdvice={generateManualSoa}
                        familyPanel={manualRow && !manualRow.error && allocation.annuity >= MIN_ANNUITY_PURCHASE ? (() => {
                            // Changing nothing is the same plan without it only when the guaranteed income is the one thing moved.
                            const only = !(allocation.funeralBond > 0) && !(allocation.gift > 0) && !(allocation.debtRepayment > 0) && !(Number(allocation.superContribution) > 0) && !(Number(allocation.investmentBond) > 0);
                            return (
                                <AnnuityFamilyPanel
                                    factFind={factFind}
                                    personName={annuityTerms.owner === 'partner' ? (factFind?.partner?.firstName || 'the partner') : clientName}
                                    amount={allocation.annuity}
                                    kind={familyPanelKindOf({ annuityType: annuityTerms.annuityType ?? 'lifetime', residualCapital: annuityTerms.residualCapital })}
                                    rows={manualRow.projections as never}
                                    compareRows={only ? (manualNothing?.projections as never) ?? null : null}
                                    compareNote={only ? 'the same plan without it, the money left in savings' : null}
                                    horizonYears={legacyYears}
                                    testId="cp-manual-annuity-family-panel"
                                />
                            );
                        })() : null}
                    />
                    {saveNote && <p className="mt-3 text-[14px] text-slate-700" role="status">{saveNote}</p>}
                </div>
            )}

            {riskPanel}
            {explainModal}
            {yearByYearModal}
        </div>
    );
};

export default CashflowPensionPage;
